import * as React from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Container,
  Card,
  Box,
  Divider,
  Typography,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { InfoCard } from "./InfoCard";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import FastRewindIcon from "@mui/icons-material/FastRewind";

const currencies = {
  EUR: "€",
  GBP: "£",
  USD: "$",
};

export const VfdCalcOutput = ({ vfdData }) => {
  React.useEffect(() => {
    if (vfdData?.results?.projectName === undefined) {
      console.log("No data available, Redirecting to home page");
      window.location.assign("/vfd");
    }
  }, [vfdData]);

  const { t, i18n } = useTranslation();
  const handleDownloadReport = () => {
    if (vfdData?.results?.url) {
      window.location.assign(vfdData.results.url);
    }
  };
  const navigate = useNavigate();
  return (
    <>
      <div style={{ marginBottom: "2rem" }}>
        <p>.</p>
      </div>

      <Container
        maxWidth={false}
        style={{
          minHeight: "100vh",
          paddingLeft: "10rem",
          marginTop: "5rem",
          marginBottom: "5rem",
          width: "80%",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ paddingX: "1rem", marginBottom: "1rem" }}
        >
          {/* Title Box */}
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                width: "115%",
                backgroundColor: "#f5f5f5",
                color: "#2676d2",
                textAlign: "center",
                padding: "1rem",
                fontSize: "1.5rem",
                fontWeight: "bold",
                borderRadius: "18px",
                border: "1px solid #2676d2",
              }}
            >
              {t("VFD Fan & Control Upgrade Results")}
            </Box>
          </Grid>

          {/* Back Button */}
          <Grid item xs={12} md={3} textAlign="right">
            <Button
              sx={{
                height: "100%",
                paddingX: "2rem",
                marginTop: "1rem",
                background: "linear-gradient(to right,rgb(2, 123, 255), rgb(113, 181, 255))",
                color: "#162c73",
                borderRadius: "8px",
                fontWeight: "bold",
                boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.42)",
                "&:hover": {
                  background: "linear-gradient(to right, rgb(141, 196, 255), rgb(190, 222, 255)))",
                },
              }}
              variant="contained"
              size="large"
              startIcon={<FastRewindIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Card
          sx={{
            p: 3,
            borderRadius: "8px",
            boxShadow: 2,
            backgroundColor: "#f5f5f5",
            border: "1px solid rgb(240, 240, 240)",
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                sx={{
                  height: "3rem",
                  fontSize: "1.1rem",
                  backgroundColor: "#007BFF",
                  color: "white",
                  borderRadius: "24px",
                  fontWeight: "bold",
                  width: "40rem",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#0056b3" },
                }}
                startIcon={<DownloadIcon />} // ← this adds the icon
                onClick={handleDownloadReport}
              >
                {t("Download Report")}
              </Button>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 1.5,
                  display: "flex",
                  width: "20rem",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  borderRadius: "18px",
                }}
              >
                <Typography
                  variant="body1"
                  fontSize="1.2rem"
                  fontWeight="bold"
                  sx={{ mr: 1 }}
                >
                  {t("ROI (Years)")}:
                </Typography>
                <Typography
                  variant="body1"
                  fontSize="1.4rem"
                  fontWeight="bold"
                  color="primary"
                >
                  {vfdData?.results?.roi || "N/A"}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("Project Description")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard
              title={t("Project Name")}
              value={vfdData?.results?.projectName}
            />
            <InfoCard
              title={t("Family Type")}
              value={t(vfdData?.results?.familyType)}
            />
            <InfoCard
              title={t("Application")}
              value={t(vfdData?.results?.application)}
            />
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("Equipment Details")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard
              title={t("Unit Size")}
              value={vfdData?.results?.unitSize}
            />
            <InfoCard
              title={t("Cooling Capacity")}
              value={vfdData?.results?.coolingCapacity}
              units="kW"
            />
            <InfoCard
              title={t("Coef mini Freq")}
              value={vfdData?.results?.coefficient_min_freq}
            />
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("Operating costs  & Return On Investment")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard
              title={t("Operating Hours / Year")}
              value={vfdData?.results?.operatingHours}
              units="h"
            />
            <InfoCard
              title={t("Electricity Cost")}
              value={vfdData?.results?.electricityCost}
              units={`${currencies[vfdData?.results?.currency] || ""}/kWh`}
            />
            {vfdData?.results?.language == "FR" && (
              <InfoCard
                title={t("CEE Value (FR)")}
                value={vfdData?.results?.ceeValorisation}
                units="€/MWh"
              />
            )}
            {vfdData?.results?.language == "FR" && (
              <InfoCard
                title={t("CEE subsidies (FR)")}
                value={vfdData?.results?.ceeSubsidies}
                units="€"
              />
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("FRENCH ENERGY CERTIFICATES SUBSIDIES (CEE)")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard title={t("Type")} value={vfdData?.results?.cee_type} />
            <InfoCard
              title={t(`${vfdData?.results?.cee_type} Freq ${t("Before")}`)}
              value={vfdData?.results?.cee_before}
            />
            <InfoCard
              title={t(`${vfdData?.results?.cee_type} Freq ${t("After")}`)}
              value={vfdData?.results?.cee_after}
            />
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("Annual Energy Consumption for the first year")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard
              title={t("Annual Energy Consumption Before")}
              value={vfdData?.results?.annual_energy_consumption_before}
              units="kWh"
            />
            <InfoCard
              title={t("Annual Energy Consumption After")}
              value={vfdData?.results?.annual_energy_consumption_after}
              units="kWh"
            />
            <InfoCard
              title={t("Annual Energy Savings")}
              value={vfdData?.results?.annual_energy_savings}
              units="kWh"
            />
            <InfoCard
              title={t("Annual Energy Cost")}
              value={vfdData?.results?.annual_energy_cost_before}
              units={currencies[vfdData?.results?.currency]}
            />
            <InfoCard
              title={t("Annual cost After Upgrade")}
              value={vfdData?.results?.annual_energy_cost_after}
              units={currencies[vfdData?.results?.currency]}
            />
            <InfoCard
              title={t("Energy Savings")}
              value={vfdData?.results?.annual_cost_savings}
              units={`${currencies[vfdData?.results?.currency] || ""}/Year`}
            />
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("CO2 emissions")}
          </Typography>
          <Grid container spacing={1}>
            <InfoCard
              title={t("CO2 Emissions Before")}
              value={vfdData?.results?.co2_emissions_before}
              units="kg CO2e"
            />
            <InfoCard
              title={t("CO2 Emissions After")}
              value={vfdData?.results?.co2_emissions_after}
              units="kg CO2e"
            />
            <InfoCard
              title={t("Annual CO2 Emission Reductions")}
              value={vfdData?.results?.co2_emissions_reduction}
              units="kg CO2e"
            />
          </Grid>
        </Card>
      </Container>
    </>
  );
};
